import { axiosInstance } from './axiosConfig.ts';
import { handleRequestWithAuth } from './refreshService.ts';

const BASE = 'facturacion/';

export interface ActividadEconomica {
  id: number;
  codigo: string;
  descripcion: string;
  tipo_actividad?: string;
  activa: boolean;
}

export interface EmpresaFacturacion {
  id: number;
  razon_social: string;
  nombre_comercial?: string;
  nit: string;
  telefono?: string;
  email?: string;
  direccion: string;
  municipio: string;
  ambiente: 1 | 2;
  codigo_sistema: string;
  codigo_modalidad: 2;
  token_delegado_configurado: boolean;
  wsdl_facturacion: string;
  wsdl_sincronizacion: string;
  wsdl_operaciones: string;
  wsdl_codigos: string;
  ruta_qr: string;
  activa: boolean;
}

const request = <T>(factory: () => Promise<{ data: T }>) =>
  handleRequestWithAuth(async () => (await factory()).data);

const listarTodo = async (ruta: string) => {
  const datos: any[] = [];
  let pagina = 1;
  while (true) {
    const paginaActual = pagina;
    const respuesta = await request<any>(() => axiosInstance.get(ruta, { params: { page: paginaActual } }));
    if (Array.isArray(respuesta)) return respuesta;
    datos.push(...(respuesta.results || []));
    if (!respuesta.next) return datos;
    pagina += 1;
  }
};

export const consultarEstadoFactura = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/${id}/consultar-estado/`));
export const emitirFacturaReserva = (cita: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/emitir-reserva/`, { cita }));
export const anularFactura = (id: number, motivo: string) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/${id}/anular/`, { motivo }));
export const revertirAnulacionFactura = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/${id}/revertir-anulacion/`));
export const reintentarCorreoFactura = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/${id}/reintentar-correo/`));
export async function descargarPDFFactura(id: number, formato: 'carta' | 'rollo') {
  try {
    const contenido = await request<Blob>(() => axiosInstance.get(`${BASE}facturas/${id}/pdf/`, { params: { formato }, responseType: 'blob' }));
    const url = URL.createObjectURL(contenido);
    const enlace = document.createElement('a'); enlace.href = url; enlace.download = `factura-${id}-${formato}.pdf`; enlace.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error: any) {
    if (error?.response?.data instanceof Blob) {
      try { const detalle = JSON.parse(await error.response.data.text()); throw new Error(detalle.detail || 'No se pudo descargar el PDF.'); }
      catch (parseError: any) { if (!(parseError instanceof SyntaxError)) throw parseError; }
    }
    throw error;
  }
}
export async function descargarXMLFactura(id: number) {
  const contenido = await request<string>(() => axiosInstance.get(`${BASE}facturas/${id}/xml/`, { responseType: 'text' }));
  const url = URL.createObjectURL(new Blob([contenido], { type: 'application/xml' }));
  const enlace = document.createElement('a'); enlace.href = url; enlace.download = `factura-${id}.xml`; enlace.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const crearCombinacionPago = (data: Record<string, unknown>) =>
  request<any>(() => axiosInstance.post(`${BASE}combinaciones-pago/`, data));
export const listarCombinacionesPago = () =>
  request<any>(() => axiosInstance.get(`${BASE}combinaciones-pago/`));
export const consultarFacturaReserva = (cita: number) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/`, { params: { cita } }));

export async function imprimirFactura(id: number) {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('Permite abrir ventanas para imprimir la factura.');
  ventana.opener = null;
  try {
    const html = await request<string>(() => axiosInstance.get(`${BASE}facturas/${id}/rollo/`, { responseType: 'text' }));
    ventana.document.open(); ventana.document.write(html); ventana.document.close();
  } catch (error) { ventana.close(); throw error; }
}

export const listarEmpresas = () => request<any>(() => axiosInstance.get(`${BASE}empresas/`));
export const crearEmpresa = (data: Record<string, unknown>) =>
  request<any>(() => axiosInstance.post(`${BASE}empresas/`, data));
export const actualizarEmpresa = (id: number, data: Record<string, unknown>) =>
  request<any>(() => axiosInstance.patch(`${BASE}empresas/${id}/`, data));
export const obtenerDetalleEmpresa = (id: number) =>
  request<any>(() => axiosInstance.get(`${BASE}empresas/${id}/detalle/`));
export const comprobarServiciosEmpresa = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}empresas/${id}/verificar-servicios/`));
export const verificarComunicacion = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}empresas/${id}/verificar-comunicacion/`));
export const verificarServicios = async (id: number) => {
  const result = await request<any>(() => axiosInstance.post(`${BASE}empresas/${id}/verificar-servicios/`));
  const errores = Object.entries(result).filter(([, valor]: any) => !valor.ok);
  if (errores.length) throw new Error(errores.map(([nombre, valor]: any) => `${nombre}: ${valor.detalle}`).join(' · '));
  return result;
};
export const subirXSD = (id: number, archivo: File) => {
  const data = new FormData();
  data.append('xsd_computarizada', archivo);
  return request<any>(() => axiosInstance.patch(`${BASE}empresas/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }));
};

export const listarActividades = () =>
  request<any>(() => axiosInstance.get(`${BASE}actividades-economicas/`));
export const crearActividad = (data: Omit<ActividadEconomica, 'id'>) =>
  request<any>(() => axiosInstance.post(`${BASE}actividades-economicas/`, data));

export const listarSucursalesFacturacion = () =>
  request<any>(() => axiosInstance.get(`${BASE}sucursales/`));
export const crearSucursalFacturacion = (data: Record<string, unknown>) =>
  request<any>(() => axiosInstance.post(`${BASE}sucursales/`, data));

export const listarPuntosFacturacion = () =>
  listarTodo(`${BASE}puntos-venta/`);
export const crearPuntoFacturacion = (data: Record<string, unknown>) =>
  request<any>(() => axiosInstance.post(`${BASE}puntos-venta/`, data));

export const listarCUIS = () => request<any>(() => axiosInstance.get(`${BASE}cuis/`));
export const solicitarCUIS = (punto_venta_facturacion: number) =>
  request<any>(() => axiosInstance.post(`${BASE}cuis/solicitar/`, { punto_venta_facturacion }));
export const solicitarCUISSucursal = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}sucursales/${id}/solicitar-cuis/`));
export const listarCUFD = () => request<any>(() => axiosInstance.get(`${BASE}cufd/`));
export const solicitarCUFD = (punto_venta_facturacion: number) =>
  request<any>(() => axiosInstance.post(`${BASE}cufd/solicitar/`, { punto_venta_facturacion }));

export const listarCatalogos = () => listarTodo(`${BASE}catalogos/`);
export const sincronizarCatalogos = (punto_venta_facturacion: number) =>
  request<any>(() => axiosInstance.post(`${BASE}catalogos/sincronizar/`, { punto_venta_facturacion }));

export const listarFacturas = () => listarTodo(`${BASE}facturas/`);
export const listarMisFacturas = (page = 1) =>
  request<{count: number; next: string | null; previous: string | null; results: any[]}>(
    () => axiosInstance.get(`${BASE}facturas/mis-facturas/`, { params: { page } }));
export const obtenerDetalleFactura = (id: number) => request<any>(() => axiosInstance.get(`${BASE}facturas/${id}/detalle/`));
export const buscarReservasPagadas = (search: string, page = 1) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/reservas-pagadas/`, { params: { search, page } }));
export const reintentarFactura = (id: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/${id}/reintentar/`));

export const listarSucursalesBase = () => request<any>(() => axiosInstance.get('sucursales/'));
export const listarPuntosBase = () => request<any>(() => axiosInstance.get('puntos-de-venta/'));
export const listarCategoriasBase = () => request<any>(() => axiosInstance.get('categories/'));
export const listarProductosBase = () => request<any>(() => axiosInstance.get('productos-cafeteria/'));
export const homologarProducto = (id: number, data: { producto_servicio_sin?: number | null; unidad_medida_sin?: number | null }) =>
  request<any>(() => axiosInstance.patch(`productos-cafeteria/${id}/`, data));
export const listarServiciosBase = () => request<any>(() => axiosInstance.get('servicios/'));
export const listarTiposPagoBase = () => request<any>(() => axiosInstance.get('tipos-pago/'));
export const listarClientesBase = () => request<any>(() => axiosInstance.get('clientes/'));
export const homologarCategoria = (id: number, actividad_economica: number) =>
  request<any>(() => axiosInstance.patch(`categories/${id}/`, { actividad_economica }));
export const homologarServicio = (id: number, data: { producto_servicio_sin?: number; unidad_medida_sin?: number }) =>
  request<any>(() => axiosInstance.patch(`servicios/${id}/`, data));
export const homologarTipoPago = (id: number, codigo_metodo_pago_sin: number) =>
  request<any>(() => axiosInstance.patch(`tipos-pago/${id}/`, { codigo_metodo_pago_sin }));
export const homologarCliente = (id: number, codigo_tipo_documento_identidad_sin: number) =>
  request<any>(() => axiosInstance.patch(`clientes/${id}/`, { codigo_tipo_documento_identidad_sin }));

export const consultarDocumentoReserva = (cita: number) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/documento-reserva/`, {params:{cita}}));
export async function imprimirPrefactura(cita: number, formato: 'carta' | 'rollo' = 'rollo') {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('Permite abrir ventanas para imprimir la prefactura.');
  ventana.opener = null;
  try {
    const html = await request<string>(() => axiosInstance.get(`${BASE}facturas/documento-reserva/`, {params:{cita, imprimir:1, formato},responseType:'text'}));
    ventana.document.open(); ventana.document.write(html); ventana.document.close();
  } catch(error) { ventana.close(); throw error; }
}


export const buscarVentasCafeteriaPagadas = (search = '', page = 1) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/cafeteria-pagadas/`, { params: { search, page } }));
export const emitirFacturaCafeteria = (venta: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/emitir-cafeteria/`, { venta }));




export const consultarDocumentoSesion = (sesion: number) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/documento-sesion/`, {params:{sesion}}));
export const consultarFacturaSesion = (sesion: number) =>
  request<any>(() => axiosInstance.get(`${BASE}facturas/`, {params:{sesion}}));
export const emitirFacturaSesion = (sesion: number) =>
  request<any>(() => axiosInstance.post(`${BASE}facturas/emitir-sesion/`, {sesion}));
export async function imprimirPrefacturaSesion(sesion: number, formato: 'carta' | 'rollo' = 'rollo') {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('Permite abrir ventanas para imprimir la prefactura.');
  ventana.opener = null;
  try {
    const html = await request<string>(() => axiosInstance.get(`${BASE}facturas/documento-sesion/`, {params:{sesion, imprimir:1, formato},responseType:'text'}));
    ventana.document.open(); ventana.document.write(html); ventana.document.close();
  } catch(error) { ventana.close(); throw error; }
}

